import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Workspace {
  key: string;
  href: string;
  labelKey: string;
  fallback: string;
}

const ROLE_WORKSPACES: Record<string, Workspace> = {
  admin: { key: "admin", href: "/admin", labelKey: "nav.adminPanel", fallback: "Admin Panel" },
  support: { key: "support", href: "/support-portal", labelKey: "nav.supportPortal", fallback: "Support Portal" },
  courier: { key: "courier", href: "/courier", labelKey: "nav.courierPortal", fallback: "Courier Portal" },
  partner: { key: "partner", href: "/partner", labelKey: "nav.partnerPortal", fallback: "Pickup Partner" },
  affiliate: { key: "affiliate", href: "/affiliate", labelKey: "nav.affiliatePortal", fallback: "Affiliate" },
};
const CRM_WORKSPACE: Workspace = { key: "crm", href: "/crm", labelKey: "nav.salesCrm", fallback: "Sales CRM" };
const ACCOUNT_WORKSPACE: Workspace = { key: "account", href: "/account", labelKey: "nav.myAccount", fallback: "My Account" };
const ORDER = ["crm", "admin", "support", "courier", "partner", "affiliate"];

/** Every portal the signed-in user may open: roles from `user_roles` plus an active CRM membership. */
export function buildWorkspaces(roles: string[], isCrm: boolean): Workspace[] {
  const list: Workspace[] = roles.map((r) => ROLE_WORKSPACES[r]).filter(Boolean);
  if (isCrm) list.push(CRM_WORKSPACE);
  const unique = Array.from(new Map(list.map((w) => [w.key, w])).values());
  unique.sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
  return [...unique, ACCOUNT_WORKSPACE];
}

export function useWorkspaces(): Workspace[] {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["workspaces", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const [rolesRes, crmRes] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user!.id),
        supabase.from("crm_users").select("role").eq("user_id", user!.id).eq("is_active", true).maybeSingle(),
      ]);
      const roles = (rolesRes.data ?? []).map((r: { role: string }) => r.role);
      return buildWorkspaces(roles, !!crmRes.data);
    },
  });
  if (data) return data;
  // Until the lookup returns, fall back to the primary role.
  return buildWorkspaces(user?.role ? [user.role] : [], false);
}
